// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { useGetReportingMoveAuditsQuery } from '../queries/getReportingMoveAudits.query.generated';

import { css } from '@emotion/css';
import { Grid, Tooltip, Typography, Theme, useTheme } from '@mui/material';
import { BiInfoCircle } from 'react-icons/bi';

import Spacer from '@components/Utils/Spacer';

import { ReportingMove } from '../types';
import { useReporting } from '../providers/ReportingProvider';
import {
  buildExecutiveKpis,
  buildKpiTrendByDay,
  isCompleted,
} from '../utils/reportingMetrics';
import { formatNumber, formatPercent } from '../utils/reportingFormatters';
import { getChartColors } from '../utils/reportingChartColors';
import ChartCard from './ChartCard';
import DashboardUnavailableNotice from './DashboardUnavailableNotice';
import { CountBarChart, KpiGauge, KpiTrendChart } from './ReportingCharts';

// TYPES ---------------------------------------------------------------- //

interface ExecutiveKpisTabProps {
  moves: ReportingMove[];
  start: string;
  end: string;
}

interface KpiGaugeCardProps {
  label: string;
  percent: number | null;
  detail: string;
  /** Plain-language definition of how the KPI is calculated, shown on hover */
  tooltip?: string;
  color: string;
  unavailableText?: string;
}

// COMPONENTS ---------------------------------------------------------------- //

/** Speedometer-style KPI card: circular gauge with a centered percentage */
const KpiGaugeCard = ({ label, percent, detail, tooltip, color, unavailableText }: KpiGaugeCardProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  return (
    <div className={cls.gaugeCard}>
      <div className={cls.gaugeLabelRow}>
        <Typography className={cls.gaugeLabel}>{label}</Typography>
        {tooltip ? (
          <Tooltip title={tooltip} placement='top' arrow>
            <span className={cls.infoIcon}>
              <BiInfoCircle size={14} />
            </span>
          </Tooltip>
        ) : null}
      </div>

      <div className={cls.gaugeBox}>
        <KpiGauge value={percent} color={color} trackColor={theme.palette.grey[200]} />
        <Typography className={cls.gaugeValue}>{formatPercent(percent)}</Typography>
      </div>

      <Typography className={cls.gaugeDetail}>{percent == null && unavailableText ? unavailableText : detail}</Typography>
    </div>
  );
};

const ExecutiveKpisTab = ({ moves, start, end }: ExecutiveKpisTabProps) => {
  const theme = useTheme();
  const colors = getChartColors(theme);
  const { isModuleVisible } = useReporting();
  const kpiVisible = (key: string) => isModuleVisible('executive', 'kpis', key);
  const chartVisible = (key: string) => isModuleVisible('executive', 'charts', key);

  // Audits are only attached to completed moves, fetched by move ID since the
  // schema exposes no moves -> moveaudit relationship
  const completedMoveIds = React.useMemo(() => moves.filter(isCompleted).map(move => move.id), [moves]);

  const { data: auditData, loading: auditsLoading, error: auditsError } = useGetReportingMoveAuditsQuery({
    variables: { moveIds: completedMoveIds },
    skip: completedMoveIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const audits = React.useMemo(() => auditData?.moveaudit || [], [auditData?.moveaudit]);

  const kpis = React.useMemo(() => buildExecutiveKpis(moves, audits), [moves, audits]);

  // Clamp the trend to today - future days have scheduled-but-unworked moves
  // that would misleadingly drag assigned/completed percentages to zero
  const trendEnd = React.useMemo(() => {
    const todayEnd = dayjs().endOf('day');
    return dayjs(end).isAfter(todayEnd) ? todayEnd.format() : end;
  }, [end]);

  const trend = React.useMemo(() => buildKpiTrendByDay(moves, audits, start, trendEnd), [moves, audits, start, trendEnd]);

  const validationBreakdown = React.useMemo(
    () => [
      { label: 'Pickup GPS Valid', count: kpis.validationBreakdown.pickupGpsValid },
      { label: 'Delivery GPS Valid', count: kpis.validationBreakdown.deliveryGpsValid },
      { label: 'Duration Valid', count: kpis.validationBreakdown.durationValid },
    ],
    [kpis.validationBreakdown]
  );

  const hasMoves = moves.length > 0;
  const validationUnavailable = !auditsLoading && kpis.auditedCompletedCount === 0;

  return (
    <>
    {/* KPI GAUGES - separate container so cards never stretch to chart row height */}
    <Grid container spacing={2}>
      {auditsError ? (
        <Grid size={{ xs: 12 }}>
          <DashboardUnavailableNotice
            severity='warning'
            message='Trip validation data could not be loaded. Assigned and completed KPIs are still shown.'
          />
        </Grid>
      ) : null}

      {/* KPI GAUGES */}

      {kpiVisible('trips_assigned') ? (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <KpiGaugeCard
            label='Trips Assigned'
            percent={kpis.assignedPercent}
            detail={`${formatNumber(kpis.assignedCount)} of ${formatNumber(kpis.totalMoves)} moves`}
            tooltip='Trips with a driver assigned or a status that implies one, excluding canceled trips, out of all trips in the date range.'
            color={colors.assigned}
            unavailableText='No moves in this date range.'
          />
        </Grid>
      ) : null}
      {kpiVisible('trips_completed') ? (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <KpiGaugeCard
            label='Trips Completed'
            percent={kpis.completedPercent}
            detail={`${formatNumber(kpis.completedCount)} of ${formatNumber(kpis.totalMoves)} moves`}
            tooltip='Trips marked delivery successful, out of all trips in the date range. Includes future scheduled trips in the denominator.'
            color={colors.completed}
            unavailableText='No moves in this date range.'
          />
        </Grid>
      ) : null}
      {kpiVisible('trips_validated') ? (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <KpiGaugeCard
            label='Trips Validated'
            percent={kpis.validatedPercent}
            detail={`${formatNumber(kpis.validatedCount)} of ${formatNumber(kpis.auditedCompletedCount)} audited completed trips`}
            tooltip='Completed trips that passed all three audit checks: pickup GPS within bounds, delivery GPS within bounds, and trip duration within bounds.'
            color={colors.validated}
            unavailableText='No audited completed trips.'
          />
        </Grid>
      ) : null}
    </Grid>

    <Spacer size={2} />

    {/* CHARTS */}
    <Grid container spacing={2}>
      {chartVisible('kpi_trend') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='KPI Trend Over Time'
            subtitle='Daily assigned, completed, and validated percentages. Percentages can swing widely on low-volume days; hover a day for raw counts.'
            empty={!hasMoves}
          >
            <KpiTrendChart data={trend} colors={colors} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('validation_breakdown') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Validation Breakdown'
            subtitle='Audited completed trips passing each check'
            empty={validationUnavailable}
            emptyMessage='No audited completed trips for this date range.'
          >
            <CountBarChart data={validationBreakdown} color={colors.validated} name='Trips' />
          </ChartCard>
        </Grid>
      ) : null}
    </Grid>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  return {
    gaugeCard: css`
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      height: 100%;
      padding: ${theme?.spacing(2)};
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      background-color: ${theme?.palette?.background?.paper};
    `,
    gaugeLabelRow: css`
      display: flex;
      align-items: center;
      gap: ${theme?.spacing(0.5)};
    `,
    gaugeLabel: css`
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 11px !important;
      font-weight: 600 !important;
      color: ${theme?.palette?.text?.secondary};
    `,
    infoIcon: css`
      display: inline-flex;
      align-items: center;
      color: ${theme?.palette?.text?.disabled};
      cursor: help;
    `,
    gaugeBox: css`
      position: relative;
      width: 100%;
      margin-top: ${theme?.spacing(1)};
    `,
    gaugeValue: css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 24px !important;
      font-weight: 700 !important;
      color: ${theme?.palette?.text?.primary};
    `,
    gaugeDetail: css`
      margin-top: ${theme?.spacing(1)} !important;
      font-size: 12px !important;
      color: ${theme?.palette?.text?.disabled};
      text-align: center;
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default ExecutiveKpisTab;
